import { ref, Ref, computed, ComputedRef } from 'vue'
import { type ExtractPropTypes } from 'vue'
import { llmModelDropdownOptions } from '@/libs/features/llm/display'
import { logLlmEventToRum } from '@/libs/features/llm/logging'
import { LlmDatadogLogEvent } from '@/libs/features/llm/types'
import { usePermissions } from '@/store/profile/permissions'
import { upsertFeedback } from './queries'
import { FeedbackScreen, LLM_FEEDBACK_PROPS, LlmFeedback } from './types'

type LlmFeedbackPropType = ExtractPropTypes<typeof LLM_FEEDBACK_PROPS>

const DEFAULT_FEEDBACK_REASONS = [
  'Incorrect',
  'Too vague',
  'Off topic',
  'Other',
]
/**
 *
 * @param props
 */
export function setup(props: LlmFeedbackPropType) {
  const { canUseLlmDevTools } = usePermissions()

  const feedbackScreen: Ref<FeedbackScreen> = ref(FeedbackScreen.COARSE)
  const coarseFeedback: Ref<boolean | undefined> = ref(undefined)
  const granularFeedback: Ref<string> = ref('')
  const customFeedbackReason: Ref<string> = ref('')
  const feedbackId: Ref<string | undefined> = ref(undefined)
  const loading: Ref<boolean> = ref(false)

  const showGranularFeedback = computed(() => {
    return (
      feedbackScreen.value === FeedbackScreen.GRANULAR ||
      !!granularFeedback.value
    )
  })
  const showCustomFeedback = computed(() => {
    return (
      feedbackScreen.value === FeedbackScreen.CUSTOM ||
      !!customFeedbackReason.value
    )
  })
  const showConfirmation = computed(() => {
    return feedbackScreen.value === FeedbackScreen.SUBMITTED
  })

  const combinedFeedback: ComputedRef<Partial<LlmFeedback>> = computed(() => ({
    ...(coarseFeedback.value !== undefined
      ? { wasHelpful: coarseFeedback.value }
      : {}),
    ...(granularFeedback.value
      ? { structuredFeedbackReason: granularFeedback.value }
      : {}),
    ...(customFeedbackReason.value
      ? { feedbackReasonDetails: customFeedbackReason.value }
      : {}),
  }))

  const datadogFeedbackDetails = computed(() => ({
    ...props.datadogLoggingDetails,
    ...combinedFeedback.value,
  }))

  /**
   *
   * @param event
   */
  async function submitFeedback(event: LlmDatadogLogEvent) {
    loading.value = true
    logLlmEventToRum(event, datadogFeedbackDetails.value)
    feedbackId.value = await upsertFeedback(
      props.rawLogId,
      feedbackId.value,
      combinedFeedback.value
    )
    loading.value = false
  }

  /**
   *
   * @param wasHelpful
   */
  async function submitCoarseFeedback(wasHelpful: boolean) {
    coarseFeedback.value = wasHelpful
    if (wasHelpful) {
      feedbackScreen.value = FeedbackScreen.SUBMITTED
    } else {
      feedbackScreen.value = FeedbackScreen.GRANULAR
    }
    await submitFeedback(LlmDatadogLogEvent.COARSE_FEEDBACK)
  }

  /**
   *
   * @param reason
   */
  async function submitGranularFeedback(reason: string) {
    granularFeedback.value = reason
    feedbackScreen.value = FeedbackScreen.CUSTOM
    await submitFeedback(LlmDatadogLogEvent.GRANULAR_FEEDBACK)
  }

  /**
   *
   * @param customFeedback
   */
  async function submitCustomFeedback(customFeedback: string) {
    customFeedbackReason.value = customFeedback.trim()
    feedbackScreen.value = FeedbackScreen.SUBMITTED
    if (customFeedbackReason.value) {
      await submitFeedback(LlmDatadogLogEvent.CUSTOM_FEEDBACK)
    }
  }

  const feedbackReasons = computed(() => [
    ...props.extraFeedbackReasons,
    ...DEFAULT_FEEDBACK_REASONS,
  ])
  return {
    canUseLlmDevTools,
    llmModelDropdownOptions,
    feedbackScreen,
    coarseFeedback,
    granularFeedback,
    customFeedbackReason,
    FeedbackScreen,
    submitCoarseFeedback,
    submitGranularFeedback,
    submitCustomFeedback,
    feedbackReasons,
    loading,
    showGranularFeedback,
    showCustomFeedback,
    showConfirmation,
  }
}
